import { inject, injectable, injectFromBase } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import { DomainSettings } from '@/decorators/domainDecorators'
import DomainBaseService from '@/services/domain/domainBaseService'
import MarketingData from '@/data/models/domain/MarketingData'
import type MarketingDataRepository from '@/data/repositories/domain/marketingData/marketingDataRepository'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type { MarketingDataSubmitPayload } from '@/services/domain/marketingData/types'
import { TenDlcTollFreeRoutes } from '@/routes/types'
import { TmApiPreconditionRequiredError } from '@/core/error/transport/tmApiPreconditionRequiredError'
import { TmApiPreconditionFailedError } from '@/core/error/transport/tmApiPreconditionFailedError'

@DomainSettings({
  model: MarketingData,
})
@injectable()
@injectFromBase()
export default class MarketingDataService extends DomainBaseService<MarketingDataRepository> {
  private readonly restrictedRoutes: string[] = [
    'payment.congratsRedirect',
    'payment.congrats',
    'payment.congratsNew',
    'user.automations.flowDetails',
    TenDlcTollFreeRoutes.tollFreeRegisterBrand,
    TenDlcTollFreeRoutes.tollFreeBase,
    TenDlcTollFreeRoutes.tollFreeRegisterCampaign,
    TenDlcTollFreeRoutes.tollFreeSummary,
    TenDlcTollFreeRoutes.tollFreeSuccess,
    TenDlcTollFreeRoutes.tollFreeInfo,
  ]

  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public getRestrictedRoutes(): string[] {
    return this.restrictedRoutes
  }

  public getMarketingData(): MarketingData | null {
    return this.getDomainRepository().getMarketingData()
  }

  // 428/412 errors mean "the action is no longer applicable" (already submitted/closed)
  private isStalePreconditionError(e: unknown): boolean {
    return e instanceof TmApiPreconditionRequiredError || e instanceof TmApiPreconditionFailedError
  }

  public async fetchMarketingData(): Promise<MarketingData | null> {
    try {
      return await this.getDomainRepository().fetchMarketingData()
    } catch (e) {
      if (this.isStalePreconditionError(e)) {
        return null
      }
      throw e
    }
  }

  public async submit(payload: MarketingDataSubmitPayload): Promise<void> {
    await this.getDomainRepository().saveMarketingData(payload)
  }

  public async skip(): Promise<void> {
    try {
      await this.getDomainRepository().skipMarketingData()
    } catch (e) {
      if (this.isStalePreconditionError(e)) {
        return
      }
      throw e
    }
  }
}
